import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from '../../components/ui/pagination';
import { Home, Plus, Edit, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const PAGE_SIZE = 10;

export default function AdminArticles() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    dek: '',
    body: '',
    category: 'features',
    heroImageUrl: '',
    tags: '',
    status: 'draft',
    founders: []
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingBodyImage, setUploadingBodyImage] = useState(false);
  const [uploadingFounderImage, setUploadingFounderImage] = useState(false);
  const [extractingMagazine, setExtractingMagazine] = useState(false);
  const [currentFounder, setCurrentFounder] = useState({ name: '', role: '', imageUrl: '', bio: '' });
  const bodyTextareaRef = React.useRef(null);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchArticles(currentPage);
  }, [token, navigate, currentPage]);

  const fetchArticles = async (page) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/articles?page=${page}&pageSize=${PAGE_SIZE}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle both paginated and non-paginated response formats
      if (response.data && response.data.items) {
        // New paginated format
        setArticles(response.data.items || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalItems(response.data.total || 0);
      } else if (Array.isArray(response.data)) {
        // Old array format (fallback)
        setArticles(response.data);
        setTotalPages(1);
        setTotalItems(response.data.length);
      } else {
        // Unknown format - reset to empty
        setArticles([]);
        setTotalPages(1);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Failed to load articles');
      setArticles([]);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Safely handle tags - might be string, array, or undefined after extraction
      let tagsArray = [];
      if (formData.tags) {
        if (typeof formData.tags === 'string') {
          tagsArray = formData.tags.split(',').map(t => t.trim()).filter(Boolean);
        } else if (Array.isArray(formData.tags)) {
          tagsArray = formData.tags;
        }
      }
      
      const payload = {
        title: formData.title || '',
        dek: formData.dek || '',
        body: formData.body || '',
        category: formData.category || 'features',
        heroImageUrl: formData.heroImageUrl || '',
        status: formData.status || 'draft',
        founders: formData.founders || [],
        tags: tagsArray
      };

      console.log('Submitting article with payload:', { ...payload, body: payload.body.substring(0, 100) + '...' });

      if (editingArticle) {
        await axios.put(`${API_URL}/api/articles/${editingArticle.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article updated successfully');
      } else {
        await axios.post(`${API_URL}/api/articles`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchArticles(currentPage);
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error(error.response?.data?.detail || 'Failed to save article');
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      dek: article.dek,
      body: article.body,
      category: article.category,
      heroImageUrl: article.heroImageUrl,
      tags: article.tags.join(', '),
      status: article.status,
      founders: article.founders || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    try {
      await axios.delete(`${API_URL}/api/articles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Article deleted successfully');
      fetchArticles(currentPage);
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Failed to delete article');
    }
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload/image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({...formData, heroImageUrl: response.data.url});
      toast.success('Image uploaded and compressed successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleBodyImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingBodyImage(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload/image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Prompt for alt text
      const altText = prompt('Enter alt text for the image (describe what the image shows):') || 'Image';
      
      // Insert image markdown at cursor position or end of content
      const textarea = bodyTextareaRef.current;
      const imageMarkdown = `\n\n![${altText}](${response.data.url})\n\n`;
      
      let newBody;
      if (textarea && textarea.selectionStart !== undefined) {
        const cursorPosition = textarea.selectionStart;
        const textBefore = formData.body.substring(0, cursorPosition);
        const textAfter = formData.body.substring(cursorPosition);
        newBody = textBefore + imageMarkdown + textAfter;
      } else {
        // If no cursor position, append to end
        newBody = formData.body + imageMarkdown;
      }
      
      setFormData(prev => ({...prev, body: newBody}));
      toast.success('Image inserted into article!');
    } catch (error) {
      console.error('Error uploading body image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingBodyImage(false);
    }
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    const text = prompt('Enter link text:');
    
    if (url && text) {
      const textarea = bodyTextareaRef.current;
      const cursorPosition = textarea.selectionStart;
      const textBefore = formData.body.substring(0, cursorPosition);
      const textAfter = formData.body.substring(cursorPosition);
      const linkMarkdown = `[${text}](${url})`;
      
      setFormData({...formData, body: textBefore + linkMarkdown + textAfter});
      toast.success('Link inserted!');
    }
  };

  const handleAddFounder = () => {
    if (!currentFounder.name || !currentFounder.role) {
      toast.error('Please enter founder name and role');
      return;
    }
    setFormData({
      ...formData,
      founders: [...formData.founders, {...currentFounder}]
    });
    setCurrentFounder({ name: '', role: '', imageUrl: '', bio: '' });
    toast.success('Founder added!');
  };

  const handleRemoveFounder = (index) => {
    const newFounders = formData.founders.filter((_, i) => i !== index);
    setFormData({...formData, founders: newFounders});
    toast.success('Founder removed');
  };

  const handleFounderImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingFounderImage(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload/image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setCurrentFounder({...currentFounder, imageUrl: response.data.url});
      toast.success('Founder image uploaded!');
      
      // Reset the file input to allow uploading the same file again or multiple uploads
      e.target.value = '';
    } catch (error) {
      console.error('Error uploading founder image:', error);
      toast.error('Failed to upload founder image');
      // Reset file input even on error
      e.target.value = '';
    } finally {
      setUploadingFounderImage(false);
    }
  };

  const handleEditFounderImage = async (founderIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingFounderImage(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload/image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update the specific founder's image
      const updatedFounders = [...formData.founders];
      updatedFounders[founderIndex].imageUrl = response.data.url;
      setFormData({...formData, founders: updatedFounders});
      
      toast.success('Founder image updated!');
      
      // Reset the file input
      e.target.value = '';
    } catch (error) {
      console.error('Error updating founder image:', error);
      toast.error('Failed to update founder image');
      // Reset file input even on error
      e.target.value = '';
    } finally {
      setUploadingFounderImage(false);
    }
  };

  const handleMagazinePageExtract = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setExtractingMagazine(true);
      toast.info('Extracting content from magazine page... This may take 10-15 seconds.');
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/admin/extract-magazine-page`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const extracted = response.data.data;
        
        // Safely handle tags - ensure it's a comma-separated string
        let tagsString = '';
        if (extracted.tags) {
          if (Array.isArray(extracted.tags)) {
            tagsString = extracted.tags.join(', ');
          } else if (typeof extracted.tags === 'string') {
            tagsString = extracted.tags;
          }
        }
        
        // Auto-fill form with extracted data including uploaded images
        setFormData({
          title: extracted.title || '',
          dek: extracted.subtitle || '',
          body: extracted.body || '',
          category: extracted.category || 'features',
          heroImageUrl: extracted.heroImageUrl || '',
          tags: tagsString,
          status: 'draft',
          founders: Array.isArray(extracted.founders) ? extracted.founders : []
        });
        
        const imageCount = extracted.extracted_images_count || 0;
        toast.success(`✨ Extraction complete! Auto-uploaded ${imageCount} images to Cloudflare`);
        
        if (imageCount > 0) {
          toast.success(`Hero image and ${extracted.founders?.length || 0} founder photos uploaded and linked!`);
        }
      }
    } catch (error) {
      console.error('Error extracting magazine page:', error);
      toast.error(error.response?.data?.detail || 'Failed to extract magazine page');
    } finally {
      setExtractingMagazine(false);
    }
  };

  const resetForm = () => {
    setEditingArticle(null);
    setFormData({
      title: '',
      dek: '',
      body: '',
      category: 'features',
      heroImageUrl: '',
      tags: '',
      status: 'draft',
      founders: []
    });
    setCurrentFounder({ name: '', role: '', imageUrl: '', bio: '' });
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5; // Show 5 page numbers at most
    
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);
    
    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  return (
    <>
      <Helmet>
        <title>Manage Articles · Admin</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur">
          <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <h1 className="font-display text-2xl font-bold">Articles</h1>
              <div className="flex items-center gap-2">
                <Button asChild variant="ghost" size="sm">
                  <Link to="/admin">
                    <Home className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display text-3xl font-bold mb-2">Manage Articles</h2>
              <p className="text-muted-foreground">
                {totalItems} total articles · Page {currentPage} of {totalPages}
              </p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="new-article-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  New Article
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingArticle ? 'Edit Article' : 'Create Article'}</DialogTitle>
                  <DialogDescription>Fill in the article details below</DialogDescription>
                </DialogHeader>
                
                {/* AI Magazine Page Extractor */}
                {!editingArticle && (
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm mb-1">✨ Extract from Magazine Page</h4>
                        <p className="text-xs text-muted-foreground mb-3">
                          Upload a PNG of your magazine page, and AI will automatically extract title, content, founders, and more!
                        </p>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={extractingMagazine}
                          onClick={() => document.getElementById('magazine-page-upload').click()}
                        >
                          {extractingMagazine ? 'Extracting... Please wait' : 'Upload Magazine Page'}
                        </Button>
                        <input
                          id="magazine-page-upload"
                          type="file"
                          accept="image/png,image/jpeg"
                          onChange={handleMagazinePageExtract}
                          className="hidden"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="dek">Summary (Dek)</Label>
                    <Textarea
                      id="dek"
                      value={formData.dek}
                      onChange={(e) => setFormData({...formData, dek: e.target.value})}
                      rows={2}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="heroImageUrl">Hero Image</Label>
                    <div className="flex gap-2 mt-1.5">
                      <Input
                        id="heroImageUrl"
                        type="url"
                        value={formData.heroImageUrl}
                        onChange={(e) => setFormData({...formData, heroImageUrl: e.target.value})}
                        placeholder="https://... or upload below"
                        className="flex-1"
                        required
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={uploadingImage}
                        onClick={() => document.getElementById('hero-image-upload').click()}
                      >
                        {uploadingImage ? 'Uploading...' : 'Upload'}
                      </Button>
                      <input
                        id="hero-image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleHeroImageUpload}
                        className="hidden"
                      />
                    </div>
                    {formData.heroImageUrl && formData.heroImageUrl.startsWith('data:image') && (
                      <p className="text-xs text-muted-foreground mt-1">✓ Compressed image ready</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="body">Body Content (Markdown supported)</Label>
                    <div className="flex gap-2 mt-1.5 mb-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={uploadingBodyImage}
                        onClick={() => document.getElementById('body-image-upload').click()}
                      >
                        {uploadingBodyImage ? 'Uploading...' : '+ Image'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={insertLink}
                      >
                        + Link
                      </Button>
                      <input
                        id="body-image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleBodyImageUpload}
                        className="hidden"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      💡 Tip: Use <code className="bg-muted px-1 rounded">![alt text](image-url)</code> for images, 
                      <code className="bg-muted px-1 rounded ml-1">[link text](url)</code> for links
                    </p>
                    <Tabs defaultValue="edit" className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="edit">Edit</TabsTrigger>
                        <TabsTrigger value="preview">Preview</TabsTrigger>
                      </TabsList>
                      <TabsContent value="edit" className="mt-2">
                        <Textarea
                          id="body"
                          ref={bodyTextareaRef}
                          value={formData.body}
                          onChange={(e) => setFormData({...formData, body: e.target.value})}
                          rows={12}
                          required
                          placeholder="Write your article content here using markdown:

**Bold text** or *italic text*

# Heading 1
## Heading 2

![Image description](https://image-url.jpg)
[Link text](https://link-url.com)

Use the buttons above to easily insert images and links."
                        />
                      </TabsContent>
                      <TabsContent value="preview" className="mt-2">
                        <div className="min-h-[300px] max-h-[400px] overflow-y-auto border rounded-md p-4 bg-[hsl(var(--surface-2))]">
                          {formData.body ? (
                            <div className="prose prose-invert max-w-none">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  p: ({node, children, ...props}) => (
                                    <p className="mb-4 text-sm leading-relaxed" {...props}>
                                      {children}
                                    </p>
                                  ),
                                  a: ({node, children, ...props}) => (
                                    <a className="text-primary hover:text-primary/80 underline" target="_blank" rel="noopener noreferrer" {...props}>
                                      {children}
                                    </a>
                                  ),
                                  img: ({node, ...props}) => (
                                    <img className="w-full rounded-lg my-4" {...props} alt={props.alt || ''} />
                                  ),
                                  h2: ({node, children, ...props}) => (
                                    <h2 className="font-display text-xl font-bold mt-6 mb-3" {...props}>
                                      {children}
                                    </h2>
                                  ),
                                  h3: ({node, children, ...props}) => (
                                    <h3 className="font-display text-lg font-bold mt-4 mb-2" {...props}>
                                      {children}
                                    </h3>
                                  ),
                                  ul: ({node, children, ...props}) => (
                                    <ul className="list-disc list-inside space-y-1 my-3 text-sm" {...props}>
                                      {children}
                                    </ul>
                                  ),
                                  ol: ({node, children, ...props}) => (
                                    <ol className="list-decimal list-inside space-y-1 my-3 text-sm" {...props}>
                                      {children}
                                    </ol>
                                  ),
                                  strong: ({node, children, ...props}) => (
                                    <strong className="font-bold text-foreground" {...props}>{children}</strong>
                                  ),
                                  em: ({node, children, ...props}) => (
                                    <em className="italic" {...props}>{children}</em>
                                  ),
                                  code: ({node, inline, children, ...props}) => (
                                    inline 
                                      ? <code className="bg-background px-1.5 py-0.5 rounded text-xs" {...props}>{children}</code>
                                      : <code className="block bg-background p-3 rounded my-3 overflow-x-auto text-xs" {...props}>{children}</code>
                                  )
                                }}
                              >
                                {formData.body}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            <p className="text-muted-foreground text-sm text-center py-12">
                              Preview will appear here as you type...
                            </p>
                          )}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="features">Features</SelectItem>
                          <SelectItem value="brands">Brands</SelectItem>
                          <SelectItem value="creators">Creators</SelectItem>
                          <SelectItem value="interviews">Interviews</SelectItem>
                          <SelectItem value="spotlights">Spotlights</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="published">Published</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Founders Section */}
                  <div className="space-y-4">
                    <Label className="text-base font-semibold">Founders/Co-founders (Optional)</Label>
                    <p className="text-sm text-muted-foreground">Add founders for brand articles</p>
                    
                    {/* List of added founders */}
                    {formData.founders && formData.founders.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {formData.founders.map((founder, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                            {founder.imageUrl && (
                              <img src={founder.imageUrl} alt={founder.name} className="w-12 h-12 rounded-full object-cover" />
                            )}
                            <div className="flex-1">
                              <p className="font-semibold">{founder.name}</p>
                              <p className="text-sm text-muted-foreground">{founder.role}</p>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => document.getElementById(`edit-founder-image-${index}`).click()}
                              disabled={uploadingFounderImage}
                            >
                              Edit Photo
                            </Button>
                            <input
                              id={`edit-founder-image-${index}`}
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleEditFounderImage(index, e)}
                              className="hidden"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveFounder(index)}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add founder form */}
                    <div className="space-y-3 p-4 border border-border rounded-lg bg-background">
                      <h4 className="font-semibold text-sm text-primary">➕ Add New Founder</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="founderName">Name</Label>
                          <Input
                            id="founderName"
                            value={currentFounder.name}
                            onChange={(e) => setCurrentFounder({...currentFounder, name: e.target.value})}
                            placeholder="John Doe"
                          />
                        </div>
                        <div>
                          <Label htmlFor="founderRole">Role</Label>
                          <Input
                            id="founderRole"
                            value={currentFounder.role}
                            onChange={(e) => setCurrentFounder({...currentFounder, role: e.target.value})}
                            placeholder="Founder & CEO"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="founderBio">Bio (Optional)</Label>
                        <Textarea
                          id="founderBio"
                          value={currentFounder.bio}
                          onChange={(e) => setCurrentFounder({...currentFounder, bio: e.target.value})}
                          placeholder="Short bio about the founder..."
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label>Photo</Label>
                        <div className="flex gap-2 items-start">
                          {currentFounder.imageUrl && (
                            <img 
                              src={currentFounder.imageUrl} 
                              alt="Preview" 
                              className="w-16 h-16 rounded-full object-cover border-2 border-border"
                            />
                          )}
                          <div className="flex-1 space-y-2">
                            <Input
                              value={currentFounder.imageUrl}
                              onChange={(e) => setCurrentFounder({...currentFounder, imageUrl: e.target.value})}
                              placeholder="Image URL or upload"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={uploadingFounderImage}
                              onClick={() => document.getElementById('founder-image-upload').click()}
                              className="w-full"
                            >
                              {uploadingFounderImage ? 'Uploading...' : '📤 Upload Photo'}
                            </Button>
                            <input
                              id="founder-image-upload"
                              type="file"
                              accept="image/*"
                              onChange={handleFounderImageUpload}
                              className="hidden"
                            />
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        onClick={handleAddFounder}
                        variant="secondary"
                        className="w-full"
                      >
                        Add Founder
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="tags">Tags (comma-separated)</Label>
                    <Input
                      id="tags"
                      value={formData.tags}
                      onChange={(e) => setFormData({...formData, tags: e.target.value})}
                      placeholder="branding, marketing, strategy"
                    />
                  </div>
                  <DialogFooter>
                    <Button type="submit">{editingArticle ? 'Update' : 'Create'}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            </div>
          ) : (
            <>
              <div className="grid gap-4" data-testid="articles-list">
                {articles.map((article) => (
                  <Card key={article.id} className="p-4 bg-[hsl(var(--surface-2))] border-border">
                    <div className="flex items-start gap-4">
                      <img src={article.heroImageUrl} alt="" className="w-32 h-20 object-cover rounded" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={article.status === 'published' ? 'default' : 'secondary'}>
                            {article.status}
                          </Badge>
                          <Badge variant="outline">{article.category}</Badge>
                        </div>
                        <h3 className="font-display text-lg font-semibold mb-1">{article.title}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">{article.dek}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {article.status === 'published' && (
                          <Button size="sm" variant="ghost" asChild>
                            <Link to={`/articles/${article.slug}`} target="_blank">
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(article)} data-testid={`edit-article-${article.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(article.id)} data-testid={`delete-article-${article.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-8">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => handlePageChange(currentPage - 1)}
                          className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                      
                      {getPageNumbers()[0] > 1 && (
                        <>
                          <PaginationItem>
                            <PaginationLink onClick={() => handlePageChange(1)} className="cursor-pointer">
                              1
                            </PaginationLink>
                          </PaginationItem>
                          {getPageNumbers()[0] > 2 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}
                        </>
                      )}
                      
                      {getPageNumbers().map(page => (
                        <PaginationItem key={page}>
                          <PaginationLink 
                            onClick={() => handlePageChange(page)}
                            isActive={currentPage === page}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                      
                      {getPageNumbers()[getPageNumbers().length - 1] < totalPages && (
                        <>
                          {getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}
                          <PaginationItem>
                            <PaginationLink onClick={() => handlePageChange(totalPages)} className="cursor-pointer">
                              {totalPages}
                            </PaginationLink>
                          </PaginationItem>
                        </>
                      )}
                      
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => handlePageChange(currentPage + 1)}
                          className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
}
